import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/store/store';
import { pickupCyclesService } from '@/pages/Business/pages/PickupCycles/services';
import { PickupBranch } from '@/pages/Business/pages/PickupCycles/models';

export type BranchesByCommerce = Record<string, PickupBranch[]>;

/**
 * Junta los commerceId distintos del listado y pide las sucursales por comercio
 * una sola vez cada uno, guardando un mapa { commerceId -> sucursales[] }.
 * Se usa dispatch(initiate) porque la cantidad de comercios es dinámica y no
 * se pueden llamar hooks en un loop.
 */
export const useBranchesMap = (
  commerceIds: string[],
  reloadToken = 0,
): { branchesByCommerce: BranchesByCommerce; isLoading: boolean } => {
  const dispatch = useDispatch<AppDispatch>();
  const [branchesByCommerce, setBranchesByCommerce] =
    useState<BranchesByCommerce>({});
  const [loadedKey, setLoadedKey] = useState('');
  const [loadedToken, setLoadedToken] = useState(0);

  const uniqueIdsKey = useMemo(
    () => Array.from(new Set(commerceIds.filter(Boolean))).sort().join(','),
    [commerceIds],
  );

  useEffect(() => {
    const ids = uniqueIdsKey ? uniqueIdsKey.split(',') : [];

    if (ids.length === 0) {
      setBranchesByCommerce({});
      setLoadedKey('');
      setLoadedToken(reloadToken);
      return;
    }

    let cancelled = false;
    const subscriptions = ids.map((commerceId) =>
      dispatch(
        pickupCyclesService.endpoints.getBranchesByCommerce.initiate(
          { commerceId },
          { forceRefetch: reloadToken > 0 },
        ),
      ),
    );

    Promise.all(
      subscriptions.map(async (subscription, index) => {
        try {
          const items = await subscription.unwrap();
          return [ids[index], items] as const;
        } catch {
          return [ids[index], [] as PickupBranch[]] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setBranchesByCommerce(Object.fromEntries(entries));
      setLoadedKey(uniqueIdsKey);
      setLoadedToken(reloadToken);
    });

    return () => {
      cancelled = true;
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [uniqueIdsKey, reloadToken, dispatch]);

  const isLoading =
    uniqueIdsKey !== '' &&
    (loadedKey !== uniqueIdsKey || loadedToken !== reloadToken);

  return { branchesByCommerce, isLoading };
};
